import type { RowSelectionState, Table } from '@tanstack/react-table'
import { useRef, useState } from 'react'

import {
  compactStatementsColumns,
  DeleteStatementsDialog,
  STATEMENT_MAX_UPLOAD_SIZE_MB,
  StatementsDataTable,
  useProject,
  useProjectMutations,
  useStatements,
} from '@/modules/projects'
import { type PresignedBlob, UploadForm } from '@/modules/upload'
import { cn } from '@/utils'
import { isProjectProcessing } from '../../status'
import { ProcessPanel } from './ProcessPanel'
import { ProjectCaseStep } from './ProjectCaseStep'
import { StepRail } from './StepRail'
import { WizardStep } from './WizardStep'

export const NewProjectWizard = () => {
  const table = useRef<Table<any> | null>(null)
  const project = useProject()
  const statements = useStatements()

  const { createStatements } = useProjectMutations()

  const [openOverrides, setOpenOverrides] = useState<Record<number, boolean>>(
    {},
  )
  const [stagedFiles, setStagedFiles] = useState(0)
  const [hasSelection, setHasSelection] = useState(false)

  const onSelectionChange = (selection: RowSelectionState) =>
    setHasSelection(Object.values(selection).length > 0)

  const hasUploaded = statements.length > 0
  const isProcessing = isProjectProcessing(project)

  // Overrides reset on upload so a step the user opened cannot stay open forever.
  const isStepOpen = (step: number) => openOverrides[step] ?? !hasUploaded

  const toggleStep = (step: number) =>
    setOpenOverrides((current) => ({ ...current, [step]: !isStepOpen(step) }))

  const onUpload = (blobs: PresignedBlob[]) =>
    createStatements(blobs).then(() => setOpenOverrides({}))

  const columns = isProcessing
    ? compactStatementsColumns.slice(1)
    : compactStatementsColumns

  const uploadSummary =
    stagedFiles > 0
      ? `${statements.length} uploaded · ${stagedFiles} not uploaded yet`
      : `${statements.length} files uploaded · Add more`

  return (
    <div className="@container w-full max-w-6xl">
      <DeleteStatementsDialog />

      <div className="grid gap-6 @4xl:grid-cols-[minmax(0,1fr)_300px]">
        <div
          className={cn(
            'col-start-1 row-start-1 flex min-w-0 flex-col',
            // clears the floating bar below the breakpoint
            hasUploaded && 'pb-24 @4xl:pb-0',
          )}
        >
          <ProjectCaseStep
            key={project?.slug}
            open={isStepOpen(1)}
            onToggle={() => toggleStep(1)}
          />

          <WizardStep
            step={2}
            title="Upload PDF statements"
            description="Upload bank statements, credit card statements, or check images."
            summary={uploadSummary}
            warning={stagedFiles > 0}
            open={isStepOpen(2)}
            onToggle={() => toggleStep(2)}
            isLast={!hasUploaded}
          >
            <div className="rounded-lg border bg-white p-6">
              <UploadForm
                disabled={isProcessing}
                onSubmit={onUpload}
                onStagedFilesChange={setStagedFiles}
                submitText="Upload PDF statements"
                maxSize={STATEMENT_MAX_UPLOAD_SIZE_MB}
              />
            </div>
          </WizardStep>

          {hasUploaded && (
            <div className="animate-slide-up-fade grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3">
              <StepRail step={3} state="pending" isLast />

              <div className="flex min-w-0 flex-col">
                <h3 className="pt-2.5 pb-4 font-semibold leading-6 tracking-tight">
                  Review statements
                </h3>

                <StatementsDataTable
                  columns={columns}
                  height="max-h-[480px]"
                  onTableReady={(value) => {
                    table.current = value
                  }}
                  onSelectionChange={onSelectionChange}
                  table={table}
                />
              </div>
            </div>
          )}
        </div>

        <div
          className={cn(
            'sticky bottom-0 z-30 col-start-1 row-start-1 self-end',
            '@4xl:static @4xl:z-auto @4xl:col-start-2 @4xl:self-stretch',
            (!hasUploaded || hasSelection) && 'hidden @4xl:block',
          )}
        >
          <ProcessPanel table={table.current} />
        </div>
      </div>
    </div>
  )
}
